import React, {useMemo} from "react";
import Form from "../../base/form";
import {Col, Row} from "reactstrap";
import Button from "../../base/button/button";
import classnames from "classnames";
import {InputForm} from "../../base/input/input";
import useCountryProvinceCache from "../../../hooks/use-country-province-cache";
import {DropdownForm} from "../../base/dropdown/dropdown";
import {MuiRFF, Yup} from '../../form';
import EnvService from "../../../../core/services/env";

const schema = Yup.object().shape({
    province: Yup.object().nullable(),
    country: Yup.object().nullable(),
    postalCode: Yup.string().trim().nullable(),
    title: Yup.string().trim().nullable(),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const FreightRegionsSearchSection = ({searchData}) => {

    const filters = useMemo(() => ({
        ...(searchData.filters ?? {}),
        country: searchData.filters?.country ? JSON.parse(searchData.filters?.country) : null,
        province: searchData.filters?.province ? JSON.parse(searchData.filters?.province) : null,
    }), [searchData.filters])

    const {countries, onCountrySelected} = useCountryProvinceCache(filters?.province?.countryId ?? filters?.country?.id);

    /**@type {Record<string, any>}*/
    const initialValues = useMemo(() => {
        return {
            province: searchData.filters?.province,
            country: countries?.find(e => e.id === searchData.filters?.province?.countryId) ?? countries?.find(e => e.id === searchData.filters?.country?.id),
            postalCode: searchData.filters?.postalCode,
            title: searchData.filters?.title,
        };
    }, [searchData.filters, countries])

    /**
     * Changes the value of province and country and if the provinces of that country is not yet loaded, gets them
     * from the server.
     *
     * @param e
     * @param form {any}
     */
    const onCountryChanged = (e, form) => {
        form.change(e.target.name, e.target.value);
        form.change('province', null);
        const countryId = e.target.value?.id;
        onCountrySelected(countryId);
    }

    /**
     * Changes the query of the url to the query containing the searched values so that the list pages of orders can
     * use them and search related items.
     * @param values {any} form values
     */
    const onSubmit = (values) => {
        searchData.setFilters(prevState => ({
            ...prevState,
            country: values.country ? JSON.stringify({...values.country, provinces: null}) : undefined,
            province: values.province ? JSON.stringify(values.province) : undefined,
            postalCode: values.postalCode ? values.postalCode : undefined,
            title: values.title ? values.title : undefined,
        }))
    }

    return (
        <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({submitting, values, form}) => {
                const colClassName = 'px-3 mb-4';
                const provinces = !!values?.country
                    ? countries?.find(e => e.id === values.country?.id)?.provinces ?? []
                    : [];
                return (
                    <Row className='align-items-center justify-content-center'>
                        <Col xs={6} className={colClassName}>
                            <DropdownForm
                                label={'Country'}
                                name={'country'}
                                required={!!required.country}
                                data={countries?.map(e => ({
                                    value: e,
                                    label: e.name,
                                    id: e.id,
                                })) ?? []}
                                onChange={(e) => onCountryChanged(e, form)}
                            />
                        </Col>
                        <Col xs={6} className={colClassName}>
                            <DropdownForm
                                label={!values.country ? 'Province / State' : values.country?.name === EnvService.USATitle ? "State" : 'Province'}
                                name={'province'}
                                required={!!required.province}
                                data={provinces?.map(e => ({
                                    value: e,
                                    label: e.name ?? e.code,
                                    id: e.id,
                                })) ?? []}
                            />
                        </Col>
                        <Col xs={6} className={classnames(colClassName)}>
                            <InputForm
                                onChange={(val) => form.change('postalCode', val)}
                                value={values?.postalCode ?? ''}
                                label={!values.country ? 'Postal/Zip Code' : values.country?.name === EnvService.USATitle ? "Zip Code" : 'Postal Code'}
                                name={'postalCode'}
                                type={'text'}
                                required={required.postalCode}
                            />
                        </Col>
                        <Col xs={6} className={classnames(colClassName)}>
                            <InputForm
                                onChange={(val) => form.change('title', val)}
                                value={values?.title ?? ''}
                                label={'Title'}
                                name={'title'}
                                type={'text'}
                                required={required.title}
                            />
                        </Col>
                        <Col xs={12}
                             className='px-3 mt-2 align-items-center row no-gutters justify-content-center align-items-center'>
                            <Button
                                disabled={!!submitting}
                                type={'submit'}
                                variant={'contained'}
                                color={'primary'}
                                className='py-3 px-8'
                            >
                                Search
                            </Button>
                        </Col>
                    </Row>
                );
            }}
        />
    );
}

export default FreightRegionsSearchSection;
